import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { filter, Subject, takeUntil } from 'rxjs';
import { PermissionService } from 'app/core/permissions/permission.service';
import { HELP_SECTIONS } from 'app/views/help-center/help-center.content';
import { HelpArticle, HelpSection } from 'app/views/help-center/help-center.types';
import { HelpArticleComponent } from 'app/views/help-center/help-article/help-article.component';
import { HelpContextService, HelpTopic } from 'app/views/help-center/help-context.service';

/**
 * Ayuda contextual: botón «?» de la barra superior que abre, en un panel lateral y sin salir de la pantalla,
 * el artículo del Centro de ayuda que corresponde a la pantalla y pestaña actuales.
 */
@Component({
    selector: 'help-panel',
    standalone: true,
    imports: [RouterLink, MatButtonModule, MatIconModule, MatTooltipModule, HelpArticleComponent],
    template: `
        @if (!inHelpCenter) {
            <button mat-icon-button (click)="toggle()" matTooltip="Ayuda de esta pantalla" aria-label="Ayuda de esta pantalla"
                [attr.aria-expanded]="opened" aria-controls="help-panel">
                <mat-icon [svgIcon]="'heroicons_outline:question-mark-circle'"></mat-icon>
            </button>
        }

        <!-- El panel va en un overlay (fuera de la cabecera, que crea su propio contexto de apilamiento) -->
        <ng-template #panelTemplate>
            <aside id="help-panel" role="dialog" aria-label="Ayuda" class="flex h-full w-full flex-col bg-card shadow-2xl">
                <div class="flex items-center justify-between border-b px-5 py-3">
                    <div class="flex items-center gap-2 text-lg font-semibold">
                        <mat-icon class="text-primary">help_center</mat-icon>
                        Ayuda
                    </div>
                    <button mat-icon-button (click)="close()" aria-label="Cerrar la ayuda">
                        <mat-icon>close</mat-icon>
                    </button>
                </div>

                <div class="flex-1 overflow-y-auto px-5 py-4">
                    @if (section && article) {
                        <div class="text-xs font-medium uppercase tracking-wide text-secondary">{{ section.title }}</div>
                        <h2 class="mb-3 mt-1 text-xl font-bold">{{ article.title }}</h2>
                        <app-help-article [article]="article" (linkClicked)="close()"></app-help-article>

                        @if (otherArticles.length) {
                            <div class="mt-6 border-t pt-4">
                                <div class="mb-2 text-sm font-semibold">Más sobre {{ section.title }}</div>
                                @for (other of otherArticles; track other.id) {
                                    <button type="button" (click)="show(other)"
                                        class="block w-full rounded px-2 py-1.5 text-left text-sm text-primary hover:bg-hover">
                                        {{ other.title }}
                                    </button>
                                }
                            </div>
                        }
                    } @else {
                        <p class="mb-4 text-secondary">Esta pantalla no tiene una ayuda específica. Elige un tema:</p>
                        @for (item of sections; track item.id) {
                            <a [routerLink]="['/ayuda', item.id]" (click)="close()"
                                class="mb-1 flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium hover:bg-hover">
                                <mat-icon class="icon-size-5 text-primary">{{ item.icon }}</mat-icon>
                                {{ item.title }}
                            </a>
                        }
                    }
                </div>

                <div class="border-t px-5 py-3">
                    <a mat-flat-button color="primary" class="w-full"
                        [routerLink]="section ? ['/ayuda', section.id] : '/ayuda'" [fragment]="article?.id" (click)="close()">
                        <mat-icon class="icon-size-5">open_in_new</mat-icon>
                        Ver en el Centro de ayuda
                    </a>
                </div>
            </aside>
        </ng-template>
    `,
})
export class HelpPanelComponent implements OnInit, OnDestroy {
    @ViewChild('panelTemplate') private _panelTemplate!: TemplateRef<unknown>;

    opened = false;
    inHelpCenter = false;

    /** Artículo de la pantalla actual */
    topic: HelpTopic | null = null;
    /** Artículo que se está leyendo en el panel (el de la pantalla u otro de su sección) */
    section: HelpSection | null = null;
    article: HelpArticle | null = null;

    sections: HelpSection[] = [];

    private _unsubscribeAll = new Subject<void>();
    private _overlayRef: OverlayRef | null = null;

    constructor(
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _helpContext: HelpContextService,
        private _router: Router,
        private _permissionService: PermissionService,
    ) {}

    ngOnInit(): void {
        this._helpContext.topic$.pipe(takeUntil(this._unsubscribeAll)).subscribe((topic) => {
            this.topic = topic;
            this.section = topic?.section ?? null;
            this.article = topic?.article ?? null;
        });

        this.inHelpCenter = this.isHelpCenter(this._router.url);
        this._router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd), takeUntil(this._unsubscribeAll))
            .subscribe((event) => {
                this.inHelpCenter = this.isHelpCenter(event.urlAfterRedirects);
                if (this.inHelpCenter) {
                    this.close();
                }
            });
    }

    ngOnDestroy(): void {
        this._overlayRef?.dispose();
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    get otherArticles(): HelpArticle[] {
        return this.section?.articles.filter((candidate) => candidate.id !== this.article?.id) ?? [];
    }

    toggle(): void {
        this.opened ? this.close() : this.open();
    }

    open(): void {
        // Cada vez que se abre, empieza por el artículo de la pantalla actual
        this.section = this.topic?.section ?? null;
        this.article = this.topic?.article ?? null;
        const isAdmin = this._permissionService.isAdmin();
        this.sections = HELP_SECTIONS.filter((section) => !section.adminOnly || isAdmin);

        if (!this._overlayRef) {
            this._overlayRef = this._overlay.create({
                positionStrategy: this._overlay.position().global().top('0').right('0'),
                height: '100%',
                width: '100%',
                maxWidth: '28rem',
                hasBackdrop: true,
                backdropClass: 'cdk-overlay-dark-backdrop',
                scrollStrategy: this._overlay.scrollStrategies.block(),
            });
            this._overlayRef.backdropClick().pipe(takeUntil(this._unsubscribeAll)).subscribe(() => this.close());
            this._overlayRef.keydownEvents().pipe(takeUntil(this._unsubscribeAll)).subscribe((event) => event.key === 'Escape' && this.close());
        }
        if (!this._overlayRef.hasAttached()) {
            this._overlayRef.attach(new TemplatePortal(this._panelTemplate, this._viewContainerRef));
        }
        this.opened = true;
    }

    close(): void {
        this._overlayRef?.detach();
        this.opened = false;
    }

    show(article: HelpArticle): void {
        this.article = article;
    }

    private isHelpCenter(url: string): boolean {
        return /^\/ayuda(\/|\?|#|$)/.test(url);
    }
}
